import { Calendar, LayoutGrid, BookOpen, Users, Sun, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type DashboardTabValue = "reservations" | "salle" | "recherche" | "clients" | "terrasse";

type Item = {
  value: DashboardTabValue;
  label: string;
  icon: typeof Calendar;
};

interface DashboardNavRailProps {
  activeTab: string;
  onChange: (value: DashboardTabValue) => void;
  terraceEnabled: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  className?: string;
}

/**
 * Left navigation rail for the Dashboard (md+).
 * - Sticky to viewport, scrolls with page only when content underneath grows beyond viewport.
 * - Collapsible: 224px expanded, 64px collapsed (icon-only). Persisted by parent.
 * - Touch targets ≥ 44px (iPad-friendly).
 */
export function DashboardNavRail({
  activeTab,
  onChange,
  terraceEnabled,
  collapsed,
  onToggleCollapsed,
  className,
}: DashboardNavRailProps) {
  const items: Item[] = [
    { value: "reservations", label: "Par jour", icon: Calendar },
    { value: "salle", label: "Plan de salle", icon: LayoutGrid },
    { value: "recherche", label: "Réservations", icon: BookOpen },
    { value: "clients", label: "Clients", icon: Users },
    ...(terraceEnabled ? [{ value: "terrasse" as const, label: "Terrasse", icon: Sun }] : []),
  ];

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        "sticky top-0 h-screen shrink-0 border-r bg-card/60 backdrop-blur-sm",
        "flex flex-col transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-56",
        className,
      )}
      aria-label="Navigation principale"
    >
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3">
        <ul className="space-y-1 px-2">
          {items.map(({ value, label, icon: Icon }) => {
            const active = activeTab === value;
            const btn = (
              <button
                type="button"
                onClick={() => onChange(value)}
                aria-current={active ? "page" : undefined}
                aria-label={label}
                className={cn(
                  "group relative flex w-full items-center gap-3 rounded-md h-11 px-3",
                  "text-sm font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  collapsed && "justify-center px-0",
                )}
              >
                {active && (
                  <span
                    aria-hidden
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-r bg-primary"
                  />
                )}
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && <span className="truncate">{label}</span>}
              </button>
            );
            return (
              <li key={value}>
                {collapsed ? (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>{btn}</TooltipTrigger>
                      <TooltipContent side="right">{label}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  btn
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="border-t p-2">
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Déployer le menu" : "Réduire le menu"}
          className={cn(
            "flex w-full items-center gap-2 rounded-md h-10 px-3 text-sm",
            "text-muted-foreground hover:bg-muted hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            collapsed && "justify-center px-0",
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="w-4 h-4 shrink-0" />
          ) : (
            <>
              <PanelLeftClose className="w-4 h-4 shrink-0" />
              <span className="truncate">Réduire</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
